import * as React from 'react';

import { useParams } from 'react-router-dom';

import MainLayout from 'src/components/layout/MainLayout';
import LoadingModal from 'src/components/navigation/LoadingModal';
import { PERMISSION } from 'src/features/auth';
import JoinParkingModal from 'src/features/dashboard/components/JoinParkingModal';
import DashboardRedirect, { type PermissionRedirect } from 'src/features/dashboard/DashboardRedirect';
import { RegularDashboard } from 'src/features/dashboard/RegularDashboard';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useGetInvitation } from 'src/features/userManagement/api/useGetInvitation';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';

const Dashboard = () => {
    const permissionHome = useHasPermissions([PERMISSION.HOME]);
    const { invitationParkingId, invitationId } = useParams();
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(true);
    const { parkingRole } = useParking();

    const { data: invitation, isLoading: isLoadingInvitation } = useGetInvitation({
        config: { enabled: !!invitationId && !!invitationParkingId },
        parkingId: invitationParkingId!,
        invitationId: invitationId!,
    });

    const { data: parking, isLoading: isLoadingParking } = useGetParking({
        config: { enabled: !!invitationId && !!invitationParkingId },
        parkingId: invitationParkingId!,
    });

    const isLoading = React.useMemo(() => {
        return !!invitationId && !!invitationParkingId && (isLoadingParking || isLoadingInvitation);
    }, [invitationId, invitationParkingId, isLoadingParking, isLoadingInvitation]);

    const permissionsForRedirect: PermissionRedirect[] = [
        {
            [PERMISSION.PARKING_DEVICES]: {
                hasPermission: useHasPermissions([PERMISSION.PARKING_DEVICES]),
                redirectPath: '/status/*',
            },
        },
        {
            [PERMISSION.VEHICLE]: {
                hasPermission: useHasPermissions([PERMISSION.VEHICLE]),
                redirectPath: '/vehicles/*',
            },
        },
        {
            [PERMISSION.STATS]: {
                hasPermission: useHasPermissions([PERMISSION.STATS]),
                redirectPath: '/statistics/*',
            },
        },
        {
            [PERMISSION.VEHICLE_VALIDATION]: {
                hasPermission: useHasPermissions([PERMISSION.VEHICLE_VALIDATION]),
                redirectPath: '/validation/*',
            },
        },
        {
            [PERMISSION.USERS]: {
                hasPermission: useHasPermissions([PERMISSION.USERS]),
                redirectPath: '/user-management/*',
            },
        },
        {
            [PERMISSION.MONITORING]: {
                hasPermission: useHasPermissions([PERMISSION.MONITORING]),
                redirectPath: '/pay-stations/*',
            },
        },
        {
            [PERMISSION.PARKING_ADMIN_SETTINGS]: {
                hasPermission: useHasPermissions([PERMISSION.PARKING_ADMIN_SETTINGS]),
                redirectPath: '/parking-admin/*',
            },
        },
        {
            [PERMISSION.PARKING_DETAILS_MANAGE]: {
                hasPermission: useHasPermissions(
                    [
                        PERMISSION.PARKING_DETAILS_MANAGE,
                        PERMISSION.PRICING,
                        PERMISSION.RESERVATIONS_SETTINGS,
                        PERMISSION.PARKING_ADMIN_SETTINGS,
                        PERMISSION.SUBSCRIPTIONS_SETTINGS,
                        PERMISSION.PARKING_NOTIFICATIONS_SETTINGS,
                        PERMISSION.ADDITIONAL_SERVICES_MANAGE,
                        PERMISSION.USERS_MANAGE,
                    ],
                    true,
                ),
                redirectPath: '/parking-management/*',
            },
        },
        {
            [PERMISSION.PROFILE]: {
                hasPermission: useHasPermissions([PERMISSION.PROFILE]),
                redirectPath: '/profile/*',
            },
        },
    ];

    return (
        <MainLayout className="gap-12">
            <LoadingModal isLoading={isLoading} />
            {invitation && parking && (
                <JoinParkingModal
                    invitation={invitation}
                    parking={parking}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                />
            )}
            {permissionHome && parkingRole !== MEMBERSHIP_ROLES.CLIENT ? (
                <RegularDashboard />
            ) : (
                <DashboardRedirect permissions={permissionsForRedirect} />
            )}
        </MainLayout>
    );
};

export default Dashboard;
